import React from 'react';
import { motion } from 'framer-motion';
import { 
  Menu, 
  Search, 
  User, 
  Settings, 
  Grid3X3, 
  List,
  Filter,
  Sun,
  Moon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';
import { refreshNotificationsToken } from '../hooks/useFcm';
import { showSuccess, showInfo } from '../utils/toast';

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
}

const Header: React.FC<HeaderProps> = ({ 
  sidebarOpen, 
  setSidebarOpen, 
  viewMode, 
  setViewMode 
}) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
      showInfo('You have been logged out successfully.');
    } catch (error) {
      // Logout will still happen even if there's an error
      showInfo('Logged out.');
    }
  };

  return (
    <motion.header
      id="app-header"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative theme-header border-b theme-border px-4 py-3 shadow-sm"
    >
      <div className="relative z-10 flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg theme-bg-tertiary hover:opacity-80 transition-all duration-150"
          >
            <Menu className="w-4 h-4 theme-text-secondary" />
          </motion.button>

          <div className="hidden md:flex items-center gap-2">
            <h1 className="text-lg font-semibold theme-text-primary leading-tight">SelectRow</h1>
          </div>
        </div>

        {/* Center Section - Search */}
        <div className="flex-1 max-w-md mx-4">
          <motion.div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 theme-text-tertiary" />
            <input
              type="text"
              placeholder="Search tasks, people, or anything..."
              className="w-full pl-10 pr-4 py-2 text-sm theme-bg-tertiary border theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-150 placeholder-gray-500 theme-text-primary"
            />
          </motion.div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            className="p-2 rounded-lg theme-bg-tertiary hover:opacity-80 transition-all duration-150"
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? (
              <Moon className="w-4 h-4 theme-text-secondary" />
            ) : (
              <Sun className="w-4 h-4 theme-text-secondary" />
            )}
          </motion.button>

          {/* View Mode Toggle */}
          {/* <div className="flex items-center theme-bg-tertiary rounded-lg p-1 gap-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-all duration-150 ${
                viewMode === 'grid'
                  ? 'bg-[#0073ea] text-white'
                  : 'theme-text-tertiary hover:theme-text-primary'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-all duration-150 ${
                viewMode === 'list'
                  ? 'bg-[#0073ea] text-white'
                  : 'theme-text-tertiary hover:theme-text-primary'
              }`}
            >
              <List className="w-4 h-4" />
            </motion.button>
          </div> */}

          {/* Filter */}
          {/* <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="p-2 rounded-lg theme-bg-tertiary hover:opacity-80 transition-all duration-150">
            <Filter className="w-4 h-4 theme-text-secondary" />
          </motion.button> */}

          {/* Notifications */}
          <NotificationBell />

          {/* Refresh Notifications (FCM) */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={async () => {
              const ok = await refreshNotificationsToken(import.meta.env.VITE_VAPID_KEY);
              if (ok) {
                showSuccess('Notifications refreshed successfully');
              } else {
                showInfo('Failed to refresh notifications');
              }
            }}
            className="px-3 py-1.5 text-xs rounded-lg theme-bg-tertiary hover:opacity-80 theme-text-secondary border theme-border"
            title="Refresh Notifications"
          >
            Refresh Notifications
          </motion.button>

          {/* User Avatar */}
          <motion.div whileHover={{ scale: 1.05 }} className="relative">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md cursor-pointer">
              <User className="w-4 h-4 text-white" />
            </div>
          </motion.div>

          {/* Logout */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            className="px-3 py-1.5 text-xs rounded-lg theme-bg-tertiary hover:opacity-80 theme-text-secondary border theme-border"
            title={user ? `Logout ${user.name}` : 'Logout'}
          >
            Logout
          </motion.button>

          {/* Settings */}
          <motion.button whileHover={{ scale: 1.05, rotate: 90 }} whileTap={{ scale: 0.95 }} className="p-2 rounded-lg theme-bg-tertiary hover:opacity-80 transition-all duration-150">
            <Settings className="w-4 h-4 theme-text-secondary" />
          </motion.button>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
